"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/password-input";
import { PhoneInput } from "@/components/phone-input";
import Link from "next/link";
import { AuthHeader } from "@/components/auth-header";
import { useAuth } from "@/contexts/AuthContext";
import { Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { EXTERNAL_LINKS } from "@/lib/links";
import { NEW_PASSWORD_MIN_LENGTH } from "@/lib/constants";
import { Feedback } from "@/components/feedback";
import { LoadingSpinner } from "@/components/loading-spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useInviteRedirect } from "@/hooks/useInviteRedirect";
import { getHomeRoute } from "@/lib/roles";
import { UserPlusIcon } from "@phosphor-icons/react/dist/ssr";
import { AuthHoneypotField } from "@/components/auth-honeypot-field";
import { honeypotRejectDelayMs, isHoneypotTripped } from "@/lib/honeypot";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { isValidInternationalPhone } from "@/lib/phone";

const createSignupSchema = (t: (key: string, values?: any) => string) =>
    z
        .object({
            email: z.email({ message: t("auth.invalidEmailAddress") }),
            name: z.string().min(1, { message: t("auth.firstNameRequired") }),
            surname: z.string().optional(),
            phone: z
                .string()
                .optional()
                .refine(
                    (val) => !val || val === "" || isValidInternationalPhone(val),
                    { message: t("auth.invalidPhoneFormat") }
                ),
            password: z.string().min(NEW_PASSWORD_MIN_LENGTH, {
                message: t("account.newPasswordMinLength", {
                    minLength: NEW_PASSWORD_MIN_LENGTH,
                }),
            }),
            confirmPassword: z.string().min(NEW_PASSWORD_MIN_LENGTH, {
                message: t("account.newPasswordMinLength", {
                    minLength: NEW_PASSWORD_MIN_LENGTH,
                }),
            }),
            acceptTerms: z.boolean().refine((val) => val === true, {
                message: t("auth.acceptTermsRequired"),
            }),
            referrer: z.string().optional(),
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: t("auth.passwordsMismatch"),
            path: ["confirmPassword"],
        });

type SignupFormValues = z.infer<ReturnType<typeof createSignupSchema>>;

function SignupForm() {
    const { signup, loading } = useAuth();
    const router = useRouter();
    const t = useTranslations();
    const [submitError, setSubmitError] = useState("");

    const schema = createSignupSchema(t);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
        watch,
    } = useForm<SignupFormValues>({
        resolver: zodResolver(schema),
        mode: "onBlur",
        defaultValues: {
            email: "",
            name: "",
            surname: "",
            phone: "",
            password: "",
            confirmPassword: "",
            acceptTerms: false,
            referrer: "",
        },
    });

    useInviteRedirect();

    const acceptTerms = watch("acceptTerms");
    usePageTitle("pageTitles.signUp");

    const onSubmit = async (data: SignupFormValues) => {
        setSubmitError("");
        try {
            if (isHoneypotTripped(data.referrer || "")) {
                await new Promise((r) =>
                    setTimeout(r, honeypotRejectDelayMs()),
                );
                setSubmitError(t("auth.registrationFailed"));
                return;
            }

            const user = await signup(
                data.email,
                data.password,
                data.name,
                data.surname || "",
                data.phone || "",
            );
            router.push(getHomeRoute(user));
        } catch (err) {
            setSubmitError(
                err instanceof Error
                    ? err.message
                    : t("auth.registrationFailed"),
            );
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="relative grid w-full grid-cols-1"
        >
            <Controller
                control={control}
                name="referrer"
                render={({ field }) => (
                    <AuthHoneypotField
                        value={field.value || ""}
                        onChange={field.onChange}
                    />
                )}
            />
            <AuthHeader heading={t("auth.createAccount")} />

            {submitError && <Feedback message={submitError} status="error" />}

            <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                    <Label>{t("common.email")}</Label>
                    <Input
                        type="email"
                        placeholder="ola@nordmann.no"
                        aria-invalid={!!errors.email}
                        {...register("email")}
                    />
                    {errors.email && (
                        <p className="text-sm text-destructive">
                            {errors.email.message}
                        </p>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>{t("common.firstName")}</Label>
                        <Input
                            placeholder="Ola"
                            aria-invalid={!!errors.name}
                            {...register("name")}
                        />
                        {errors.name && (
                            <p className="text-sm text-destructive">
                                {errors.name.message}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label>
                            {t("common.lastName")} ({t("common.optional")})
                        </Label>
                        <Input
                            placeholder="Nordmann"
                            aria-invalid={!!errors.surname}
                            {...register("surname")}
                        />
                        {errors.surname && (
                            <p className="text-sm text-destructive">
                                {errors.surname.message}
                            </p>
                        )}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>
                        {t("common.phoneNumber")} ({t("common.optional")})
                    </Label>
                    <Controller
                        control={control}
                        name="phone"
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value || ""}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!errors.phone}
                            />
                        )}
                    />
                    {errors.phone && (
                        <p className="text-sm text-destructive">
                            {errors.phone.message}
                        </p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label>{t("common.password")}</Label>
                    <p className="text-sm text-muted-foreground">
                        {t("account.newPasswordMinLength", {
                            minLength: NEW_PASSWORD_MIN_LENGTH,
                        })}
                    </p>
                    <Controller
                        control={control}
                        name="password"
                        render={({ field }) => (
                            <PasswordInput
                                value={field.value || ""}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                placeholder={t("common.enterPassword")}
                                autoComplete="new-password"
                            />
                        )}
                    />
                    {errors.password && (
                        <p className="text-sm text-destructive">
                            {errors.password.message}
                        </p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label>{t("auth.confirmPassword")}</Label>
                    <Controller
                        control={control}
                        name="confirmPassword"
                        render={({ field }) => (
                            <PasswordInput
                                value={field.value || ""}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                placeholder={t(
                                    "auth.confirmPasswordPlaceholder",
                                )}
                                autoComplete="new-password"
                            />
                        )}
                    />
                    {errors.confirmPassword && (
                        <p className="text-sm text-destructive">
                            {errors.confirmPassword.message}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                        <Controller
                            control={control}
                            name="acceptTerms"
                            render={({ field }) => (
                                <Checkbox
                                    id="acceptTerms"
                                    name="toupp"
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    className="mt-0.5"
                                />
                            )}
                        />
                        <Label
                            htmlFor="acceptTerms"
                            className="text-sm font-normal leading-snug"
                        >
                            {t.rich("auth.acceptTerms", {
                                termsLink: (chunks) => (
                                    <Link
                                        href={EXTERNAL_LINKS.TERMS_OF_USE}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold text-primary hover:underline"
                                    >
                                        {chunks}
                                    </Link>
                                ),
                                privacyLink: (chunks) => (
                                    <Link
                                        href={EXTERNAL_LINKS.PRIVACY_POLICY}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold text-primary hover:underline"
                                    >
                                        {chunks}
                                    </Link>
                                ),
                            })}
                        </Label>
                    </div>
                    {errors.acceptTerms && (
                        <p className="text-sm text-destructive">
                            {errors.acceptTerms.message}
                        </p>
                    )}
                </div>
                <Button
                    type="submit"
                    variant="default"
                    className="w-full"
                    disabled={loading || isSubmitting || !acceptTerms}
                >
                    <UserPlusIcon weight="bold" className="size-5" />
                    {loading || isSubmitting
                        ? t("auth.creatingAccount")
                        : t("auth.createAccount")}
                </Button>
                <p className="text-sm text-muted-foreground">
                    {t("auth.alreadyHaveAccount")}{" "}
                    <Link
                        href="/auth/signin/email"
                        className="font-semibold text-primary hover:underline"
                    >
                        {t("auth.signIn")}
                    </Link>
                </p>
            </div>
        </form>
    );
}

export default function SignupPage() {
    return (
        <Suspense
            fallback={
                <div className="flex w-full justify-center py-8">
                    <LoadingSpinner />
                </div>
            }
        >
            <SignupForm />
        </Suspense>
    );
}
